import React from 'react';
import { Routes, Route, useLocation, useParams, Navigate } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import Box from '@mui/material/Box';
import theme from './theme';
import Sidebar, { SIDEBAR_WIDTH } from './components/Sidebar';
import ExplorerDashboard from './pages/ExplorerDashboard';
import ApplicationsPage from './pages/ApplicationsPage';
import ProfileManagerPage from './pages/ProfileManagerPage';
import ProfileEnhancementPage from './pages/ProfileEnhancementPage';
import EnhancementFlowPage from './pages/EnhancementFlowPage';
import ApplicationDetailPage from './pages/ApplicationDetailPage';
import ProjectDetailPage from './pages/ProjectDetailPage';
import ProfileTimelinePage from './pages/ProfileTimelinePage';
import InterviewPage from './pages/InterviewPage';
import CompaniesDiscoveryPage from './pages/CompaniesDiscoveryPage';
import CompanyInsightsPage from './pages/CompanyInsightsPage';
import CourseCatalogPage from './pages/CourseCatalogPage';
import CourseDetailPage from './pages/CourseDetailPage';
import JobFamilyHubPage from './pages/JobFamilyHubPage';
import JobFamilyPage from './pages/JobFamilyPage';
import JobPostingPreviewPage from './pages/JobPostingPreviewPage';
import PreferencesPage from './pages/PreferencesPage';
import SignupJourneyPage from './pages/SignupJourneyPage';
import ProfileSharePage from './pages/ProfileSharePage';
import ShareAdminPage from './pages/ShareAdminPage';
import AdminPage from './pages/AdminPage';
import AuthGate from './components/auth/AuthGate';
import ViewAsBanner from './components/admin/ViewAsBanner';

/**
 * Redirect helper for legacy routes — substitutes :param tokens from the
 * matched URL into the target path, and preserves the query string. Used
 * to keep old /companies and /jobs/families links working after we moved
 * everything under /explore.
 */
function LegacyRedirect({ to }) {
  const params = useParams();
  const location = useLocation();
  let target = to;
  for (const [k, v] of Object.entries(params)) {
    target = target.replace(`:${k}`, v);
  }
  return <Navigate to={target + location.search} replace />;
}

function App() {
  const location = useLocation();

  // Prototype route — bypasses AuthGate so we can iterate on the UX while
  // already signed into the main app. Swap into AuthGate flow once ready.
  if (location.pathname === '/signup-v2') {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <SignupJourneyPage />
      </ThemeProvider>
    );
  }

  // Profile Share — PUBLIC. Deliberately handled before AuthGate, because the
  // recipient is a recruiter with no Onward account: inside the gate they'd be
  // shown the landing page instead of the thing they were sent.
  //
  // This is not only a gate bypass, it is a different *shell*. Returning here
  // means no Sidebar, no ViewAsBanner, and — importantly — no useAuth call, so
  // the page never asks the API who the visitor is. Access is the token in the
  // URL and nothing else, which is exactly what the backend enforces.
  //
  // Keep this above AuthGate. Moving the route inside <Routes> would silently
  // make every share link resolve to the landing page for its intended reader.
  if (location.pathname.startsWith('/profileshare/')) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Routes>
          <Route path="/profileshare/:slug/:token" element={<ProfileSharePage />} />
          {/* A malformed share URL must look exactly like a withdrawn one. */}
          <Route path="/profileshare/*" element={<ProfileSharePage />} />
        </Routes>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthGate>
      <ViewAsBanner />
      <Box sx={{ display: 'flex', minHeight: '100vh' }}>
        <Sidebar />
        <Box component="main" sx={{ flexGrow: 1, p: 3, ml: `${SIDEBAR_WIDTH}px` }}>
          <Routes>
            <Route path="/" element={<ExplorerDashboard />} />

            {/* Canonical routes — companies + roles live under /explore.
                These are "learn the landscape" surfaces (vs. /jobs which
                will eventually serve specific open postings + bulk-apply,
                currently hidden behind JobsBrowserPage). */}
            <Route path="/explore/roles" element={<JobFamilyHubPage />} />
            <Route path="/explore/roles/:familyId" element={<JobFamilyPage />} />
            <Route path="/explore/companies" element={<CompaniesDiscoveryPage />} />
            {/* Legacy /discovery path → canonical /explore/companies (post-swap) */}
            <Route path="/explore/companies/discovery" element={<Navigate to="/explore/companies" replace />} />
            <Route path="/courses" element={<CourseCatalogPage />} />
            <Route path="/courses/:code" element={<CourseDetailPage />} />
            <Route path="/explore/companies/:domain" element={<CompanyInsightsPage />} />

            {/* Legacy redirects — saved links keep working. Drop these once
                we're confident no live links to the old paths remain. */}
            <Route path="/jobs" element={<Navigate to="/explore/roles" replace />} />
            <Route path="/jobs/families" element={<Navigate to="/explore/roles" replace />} />
            <Route path="/jobs/families/:familyId" element={<LegacyRedirect to="/explore/roles/:familyId" />} />
            <Route path="/companies" element={<Navigate to="/explore/companies" replace />} />
            <Route path="/companies/discovery" element={<Navigate to="/explore/companies" replace />} />
            <Route path="/companies/:domain" element={<LegacyRedirect to="/explore/companies/:domain" />} />

            <Route path="/preferences" element={<PreferencesPage />} />
            <Route path="/applications" element={<ApplicationsPage />} />
            <Route path="/profile" element={<ProfileManagerPage />} />
            <Route path="/profile/enhance" element={<ProfileEnhancementPage />} />
            <Route path="/enhancement" element={<EnhancementFlowPage />} />
            <Route path="/enhancement/:conversationId" element={<EnhancementFlowPage />} />
            {/* Capabilities-aware profile interview — dedicated-page shell */}
            <Route path="/interview" element={<InterviewPage />} />
            {/* Profile timeline view */}
            <Route path="/profile/timeline" element={<ProfileTimelinePage />} />
            {/* Project detail page */}
            <Route path="/projects/:projectId" element={<ProjectDetailPage />} />
            {/* Read-only posting preview — no Application is created on view.
                "Start Application" on the page bridges into the full flow. */}
            <Route path="/applications/preview/:companyDomain/:jobId" element={<JobPostingPreviewPage />} />
            {/* Application detail page - NEW unified view */}
            <Route path="/applications/:id" element={<ApplicationDetailPage />} />
            {/* NOTE: the legacy /applications/:id/workflow route was removed.
                ApplicationDetailPage is the canonical detail/flow view. The old
                ApplicationWorkflowPage + components/workflow/* +
                components/application/stages/* + services/workflowService.js are
                now fully orphaned and can be bulk-deleted once submission is
                confirmed covered by ApplicationDetailPage. */}
            <Route path="/admin" element={<AdminPage />} />
            {/* Share authoring — admin-gated server-side too. */}
            <Route path="/admin/shares" element={<ShareAdminPage />} />
          </Routes>
        </Box>
      </Box>
      </AuthGate>
    </ThemeProvider>
  );
}

export default App;
